import { useCallback, useState } from 'react'
import * as React from 'react'

import dayjs from 'dayjs'

import { getCookieAsParsedJson } from '~/utils/cookie'
import {
  dayjsToString,
  getDailyDateRange,
  getPossibleDailySlots,
  getPossibleFERSlots,
  getSlotFromTime,
} from '~/utils/graphqlDataFormatters'
import { isDaily, isEvent, isFER } from '~/utils/isReservableType'

import { ALL_DAY_SLOT } from '~/components/Festival/FERModal/FERModalUtils'
import { getRangeStart } from '~/components/Listing/pages/FERListingPage/FERListingPageUtils'
import { EarlyReservationCookie } from '~/components/Reservation/EarlyReservationPage/EarlyReservationPageTypes'
import { getDefaultFestivalMenuCounts } from '~/components/Reservation/SlotSelectionPanel/SlotSelectionPanelUtils'
import SlotSelectionPanel, { SelectionPanelViewProps } from '~/components/Reservation/SlotSelectionPanel/SlotSelectionPanelView'
import { PresaleCodeInputView } from '~/components/Restaurant/PresaleCodeInput/PresaleCodeInputView'
import { CollapseDescription, CollapseHeading, Title } from '~/components/Restaurant/RestaurantPage/RestaurantPageShards'
import { getCollapseData } from '~/components/Restaurant/RestaurantPage/RestaurantPageUtils'
import { useCreateReservation } from '~/components/Restaurant/RestaurantReservables/RestaurantReservablesUtils'
import { RESERVABLE_TYPES } from '~/constants'
import {
  CreateDailyReservationMutation,
  CreateFestivalReservationMutation,
  Daily,
  FestivalEditionRestaurant,
  FestivalStateEnum,
  LeanMenuFragment,
  ReservableEnum,
  ReservablesQuery,
} from '~/generated/graphql'
import { gt } from '~/locale'
import RestaurantCollapse from '~/shared/atoms/RestaurantCollapse'

import { RestaurantMenuTooltipView } from '../components'

export type SlotListReservable = ReservablesQuery['reservables'][number] & { name?: string }

type BaseSlotsProps = Pick<SelectionPanelViewProps, 'defaultSlot' | 'defaultDate' | 'onSubmit' | 'name'>

type FERSlotsProps = BaseSlotsProps & {
  reservable: SlotListReservable & Required<Pick<FestivalEditionRestaurant, '__typename'>>
}

const FERSlots = ({ reservable, defaultSlot, defaultDate, onSubmit, name }: FERSlotsProps) => {
  return (
    <SlotSelectionPanel
      forceSearchParams
      reservableInput={{
        id: reservable.id,
        type: ReservableEnum.FestivalEditionRestaurant,
      }}
      withReservableExtra={name === 'chm'}
      fieldsToHide={['title', 'name', 'submit', 'slot']}
      type={RESERVABLE_TYPES.FER}
      dateRange={{
        startsOn: dayjsToString(
          getRangeStart(dayjs.parseZone(reservable.festivalEdition.startsAt), dayjs.parseZone(reservable.festivalEdition.reservableFrom))
        ),
        endsOn: dayjsToString(dayjs.parseZone(reservable.festivalEdition.endsAt)),
      }}
      possibleSlots={reservable.allDaySlots ? [ALL_DAY_SLOT] : getPossibleFERSlots(reservable)}
      onSubmit={onSubmit}
      defaultDate={defaultDate}
      defaultSlot={defaultSlot}
      minimumPeopleCount={reservable.festivalEdition.minPeopleCount}
      name={name}
      forceUpdateQuery
    />
  )
}

type D2DSlotsProps = BaseSlotsProps & {
  reservable: SlotListReservable & Required<Pick<Daily, '__typename'>>
}

const D2DSlots = ({ reservable, defaultSlot, defaultDate, onSubmit, name }: D2DSlotsProps) => {
  return (
    <SlotSelectionPanel
      forceSearchParams
      reservableInput={{
        id: reservable.id,
        type: ReservableEnum.Daily,
      }}
      withReservableExtra={name === 'chm'}
      fieldsToHide={['name', 'submit']}
      type={RESERVABLE_TYPES.DAILY}
      dateRange={getDailyDateRange(dayjs.parseZone())}
      possibleSlots={getPossibleDailySlots(reservable)}
      onSubmit={onSubmit}
      defaultDate={defaultDate}
      defaultSlot={defaultSlot}
      name={name}
      minimumPeopleCount={reservable.minPeopleCount}
      forceUpdateQuery
    />
  )
}

type RestaurantReservablesProps = {
  reservables: SlotListReservable[]
  defaultCollapse?: string
  onReservationCreated: (
    reservation: CreateFestivalReservationMutation['createFestivalReservation'] | CreateDailyReservationMutation['createDailyReservation'],
    isCHMReservation: boolean
  ) => void
  onSectionCollapse: () => void
  hasReservableExtras: boolean
}

const RestaurantReservablesView = ({
  reservables,
  defaultCollapse,
  onReservationCreated,
  onSectionCollapse,
  hasReservableExtras,
}: RestaurantReservablesProps) => {
  const [openCollapse, setOpenCollapse] = useState<string | null>(defaultCollapse ?? null)
  const earlyReservation = getCookieAsParsedJson(undefined, 'earlyReservationVoucher') as EarlyReservationCookie
  const [presaleCode, setPresaleCode] = useState('')

  const onCompleted = useCallback(
    (result: CreateFestivalReservationMutation | CreateDailyReservationMutation, isCHMReservation: boolean = false) => {
      if (!result) return
      const reservation = 'createFestivalReservation' in result ? result.createFestivalReservation : result.createDailyReservation
      onReservationCreated(reservation, isCHMReservation)
    },
    [onReservationCreated]
  )

  const { createDailyReservation, createFestivalReservation } = useCreateReservation()

  return (
    <>
      {reservables
        .reduce((acc, curr) => {
          if (isDaily(curr) && hasReservableExtras) {
            acc.push({ ...curr, name: 'chm' })
          } else if (!isDaily(curr)) {
            acc.push(curr)
          }

          return acc
        }, [] as SlotListReservable[])
        .concat([reservables.find(reservable => isDaily(reservable))].filter(Boolean) as SlotListReservable[])
        .map(untypedReservable => {
          if (isEvent(untypedReservable)) {
            return null
          }

          // we've already covered the case where reservable is an Event, but TS doesn't know this
          const reservable = untypedReservable as SlotListReservable & {
            __typename: NonNullable<Exclude<SlotListReservable['__typename'], 'Event'>>
          }

          const isFer = isFER(reservable)
          let name = isFer ? reservable.festivalEdition.festival.slug : 'daily'
          if (reservable?.name) {
            name = reservable.name
          }
          const isOpen = openCollapse === name
          const collapseData = getCollapseData(name)

          const showPresale =
            isFer &&
            reservable.festivalEdition.state === FestivalStateEnum.Presale &&
            (!earlyReservation || earlyReservation.festivalId !== reservable.festivalEdition.id)

          const getVoucherCode = () => {
            if (!isFer) return undefined
            if (showPresale) return presaleCode

            if (earlyReservation && earlyReservation.festivalId === reservable.festivalEdition?.id) return earlyReservation.code
          }

          const getDefaultSlot = () => {
            if (isFer) return getSlotFromTime(defaultDate)
            return getSlotFromTime(defaultDate)
          }

          const defaultDate = dayjs.parseZone(reservable.reservableFrom)
          const defaultSlot = getDefaultSlot()

          const handleToggle = async (name: string) => {
            const wasSectionOpenedWithoutClosingPrev = name !== openCollapse && openCollapse !== null
            if (wasSectionOpenedWithoutClosingPrev) {
              await onSectionCollapse()
            }

            setOpenCollapse(prevState => {
              const newState = prevState === name ? null : name
              if (!newState) {
                onSectionCollapse()
              }
              return newState
            })
          }

          const commonSlotsProps = {
            defaultSlot,
            defaultDate,
            name,
          }

          const isPresaleValid = !showPresale || presaleCode
          return (
            <RestaurantCollapse
              key={reservable.id}
              title={
                <Title>
                  {isFer && (
                    <RestaurantMenuTooltipView
                      menus={reservable.menus as LeanMenuFragment[]}
                      forceLabels={reservable.festivalEdition?.forceLabels}
                      showMenuValue={reservable.id}
                      style={{ alignSelf: 'flex-start' }}
                    />
                  )}
                  <div>
                    <CollapseHeading>{gt.tp('RestaurantPage', collapseData.name)}</CollapseHeading>
                    <CollapseDescription>{gt.tp('RestaurantPage', collapseData.description)}</CollapseDescription>{' '}
                    {/* remember to add also to noop.js */}
                  </div>
                </Title>
              }
              isOpen={isOpen}
              onToggle={() => handleToggle(name)}
              size='big'>
              {showPresale && <PresaleCodeInputView onSubmit={setPresaleCode} value={presaleCode} />}
              {isFer && isPresaleValid && (
                <FERSlots
                  {...commonSlotsProps}
                  reservable={reservable}
                  onSubmit={searchParams =>
                    createFestivalReservation({
                      variables: {
                        ...searchParams,
                        date: dayjsToString(searchParams.date),
                        festivalEditionId: reservable.festivalEdition.id,
                        restaurantId: reservable.restaurant.id,
                        menus: getDefaultFestivalMenuCounts(reservable.menus || []),
                        voucherCode: getVoucherCode(),
                      },
                      onCompleted: reservation => onCompleted(reservation, name === 'chm'),
                    })
                  }
                />
              )}
              {isDaily(reservable) && (
                <D2DSlots
                  {...commonSlotsProps}
                  reservable={reservable}
                  onSubmit={searchParams =>
                    createDailyReservation({
                      variables: {
                        ...searchParams,
                        date: dayjsToString(searchParams.date),
                        restaurantId: reservable.restaurant.id,
                      },
                      onCompleted: reservation => onCompleted(reservation, name === 'chm'),
                    })
                  }
                />
              )}
            </RestaurantCollapse>
          )
        })}
    </>
  )
}

export default RestaurantReservablesView
